import React from "react";
import { AiSessionMessage } from "../types/ai-session.entity";
import { useGetAISessionMessagesQuery } from "../ai-sessions.service";

type Props = {
  sessionId: string;
};

const formatMessageTimestamp = (timestamp?: AiSessionMessage["timestamp"]) => {
  if (!timestamp) return "Unknown time";

  const date = new Date(timestamp?.toDate?.() || timestamp);
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getMessageContent = (message: AiSessionMessage) => {
  const content = message.content?.trim();
  if (content) return content;

  if (message.role === "TOPIC_CHANGE") {
    return message.topicId || "Topic Change";
  }

  return "No content";
};

const roleBadgeClassByRole: Record<AiSessionMessage["role"], string> = {
  AI: "bg-slate-100 text-slate-700 border-slate-200",
  USER: "bg-sky-100 text-sky-700 border-sky-200",
  TOPIC_CHANGE: "bg-amber-100 text-amber-700 border-amber-200",
};

const roleLabelByRole: Record<AiSessionMessage["role"], string> = {
  AI: "AI",
  USER: "User",
  TOPIC_CHANGE: "Topic Change",
};

const MessageRow = ({ message }: { message: AiSessionMessage }) => {
  if (message.role === "TOPIC_CHANGE") {
    return (
      <div className="py-2">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-amber-200" />
          <div className="flex flex-col items-center gap-1">
            <span className="inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200">
              {getMessageContent(message)}
            </span>
            <span className="text-[11px] text-gray-500">{formatMessageTimestamp(message.timestamp)}</span>
          </div>
          <div className="h-px flex-1 bg-amber-200" />
        </div>
      </div>
    );
  }

  const isUserMessage = message.role === "USER";

  return (
    <div className={`flex ${isUserMessage ? "justify-end" : "justify-start"}`}>
      <div
        className={
          "w-full max-w-3xl rounded-2xl p-4 " +
          (isUserMessage
            ? "bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-md shadow-black/10"
            : "bg-white text-slate-800 shadow-md shadow-black/10")
        }
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <span
            className={
              "inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-semibold " +
              (isUserMessage
                ? "bg-white/15 text-white border-white/20"
                : roleBadgeClassByRole[message.role])
            }
          >
            {roleLabelByRole[message.role]}
          </span>
          <span className={`text-[11px] ${isUserMessage ? "text-sky-50" : "text-gray-500"}`}>
            {formatMessageTimestamp(message.timestamp)}
          </span>
        </div>

        <p className={`m-0 whitespace-pre-wrap break-words text-sm leading-6 ${!message.content?.trim() ? "italic opacity-80" : ""}`}>
          {getMessageContent(message)}
        </p>

        {message.explanation?.trim() && (
          <div className={`mt-3 rounded-xl border p-3 text-sm ${isUserMessage ? "border-white/20 bg-white/10 text-sky-50" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
            {message.explanation}
          </div>
        )}

        {typeof message.accuracyPercentage === "number" && (
          <div className="mt-3">
            <span
              className={
                "inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-semibold " +
                (isUserMessage
                  ? "border-white/20 bg-white/10 text-sky-50"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700")
              }
            >
              Accuracy: {message.accuracyPercentage.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const AISessionMessages: React.FC<Props> = ({ sessionId }) => {
  const { data: messages = [], isLoading, isError } = useGetAISessionMessagesQuery({ sessionId });

  return (
    <section className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-semibold m-0">AI Session Messages</h2>
      </div>

      <div className="p-6">
        {isLoading && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-slate-500">
            Loading messages...
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700">
            Failed to load messages.
          </div>
        )}

        {!isLoading && !isError && messages.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-slate-500">
            No messages found
          </div>
        )}

        {!isLoading && !isError && messages.length > 0 && (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageRow key={message.id} message={message} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AISessionMessages;
